import React, { useEffect, useRef, useState } from 'react';

import { isEqual } from 'lodash';
import { Grid, Box, Typography } from '@material-ui/core';
import { ValidatorForm } from 'react-material-ui-form-validator';
import { ErrorOutline } from '@material-ui/icons';

import { CardComponent, ColoredBox, ImageChooseComponent, SettingsForm } from 'components';
import { useSubmittingState } from 'hooks';
import { PlanogramExtended, SphereTemplate } from 'types';
import { picturesModel } from 'models';
import { IMAGE_UPLOAD_CAPTION } from 'components/constants';
import { TextValidatorComponent } from 'components/FormComponents';
import { DefaultTemplateDialog } from 'components/Dialogs';
import AlertDialog from 'components/Dialogs/AlertDialog/AlertDialog';
import { VALIDATORS_MAP } from 'components/FormComponents/const';
import { PLANOGRAM_VERSION_STATUS } from 'pages/planograms/constants';
import { COLORS } from 'theme/constants';
import { templateSettingsModel } from 'models/TemplateSettingsModel';
import { useBusyProgressActions } from 'contexts/BusyProgressContext';

import { InfoOutlinedIcon } from 'components/InfoTooltip/style';
import { ImageUploadCaption, Switch } from 'components/style';

const FORM_ID = 'templateSettings';

interface TemplateSettingsProps {
  planogram: PlanogramExtended;
  planogramId: number;
  handleServerError: (err: any) => void;
  setSuccessActionMessages: (messages: string[]) => void;
}

const TemplateSettings = ({
  planogram,
  planogramId,
  handleServerError,
  setSuccessActionMessages
}: TemplateSettingsProps) => {
  const formRef = useRef<ValidatorForm>(null);
  const [settings, setSettings] = useState<SphereTemplate>(planogram.sphereTemplate);
  const [initialSettings, setInitialSettings] = useState(planogram.sphereTemplate);
  const [templatesList, setTemplatesList] = useState<SphereTemplate[]>([]);
  const [toggleDialog, setToggleDialog] = useState(false);
  const [showError, setShowError] = useState(false);
  const { withPageProgressHandler } = useBusyProgressActions();
  const { isSubmitting, withHandlingSubmittingState } = useSubmittingState();

  useEffect(() => {
    getTemplateList();
  }, []);

  const getTemplateList = withPageProgressHandler(() =>
    templateSettingsModel
      .getSettings()
      .then(data => setTemplatesList(data.sphereTemplates))
      .catch(handleServerError)
  );

  const getImages = (params: any) => picturesModel.getPictures(params).catch(handleServerError);

  const isPublished = !planogram.planogramVersions.some(pl => pl.status === PLANOGRAM_VERSION_STATUS.published);

  const onSubmit = withHandlingSubmittingState(() => {
    const dataToSave: SphereTemplate = {
      ...settings,
      pictureId: settings.picture?.id ?? null,
      planogramId,
      isDefault: settings.isDefault ?? false,
    };

    if (dataToSave.pictureId === null) {
      setShowError(true);
      return Promise.resolve();
    }

    const request = dataToSave.id ? templateSettingsModel.updateSettings : templateSettingsModel.saveSettings;

    return request(dataToSave)
      .then(data => {
        setSuccessActionMessages(data.success);
        setSettings(data.sphereTemplate);
        setInitialSettings(data.sphereTemplate);
      })
      .catch(handleServerError)
  });

  const handleToggle = ({ target: { checked } }: React.ChangeEvent<HTMLInputElement>) => {
    handleChange({ isEnabled: checked });
  };

  const handleChange = (item: Partial<SphereTemplate>) => {
    setSettings(prevState => ({
      ...prevState,
      ...item,
    }));
  };

  const handleValidation = (isValid: boolean) => setShowError(!isValid);

  const isSaveButtonVisible = !isEqual(settings, initialSettings);

  return (
    <>
      <SettingsForm
        isSaveButtonVisible={isSaveButtonVisible}
        handleSave={onSubmit}
        id={FORM_ID}
        refForm={formRef}
      >
        <CardComponent
          title="Template"
          isLoading={isSubmitting}
          CardHeaderNav={() => <Switch disabled={isPublished} checked={settings?.isEnabled} onChange={handleToggle} />}
        >
          {settings?.isEnabled && (
            <Grid container spacing={5}>
              <Grid item sm={5}>
                <ImageChooseComponent
                  image={settings.picture}
                  setImage={val => handleChange({ picture: val })}
                  handleServerError={handleServerError}
                  dialogTitle="Choose thumbnail"
                  getPictures={getImages}
                  className="template-preview"
                />
              </Grid>
              <Grid item sm={7}>
                <ImageUploadCaption style={{ display: 'flex' }}>
                  <InfoOutlinedIcon className="regular alignMiddle" />
                  {IMAGE_UPLOAD_CAPTION.template}
                </ImageUploadCaption>
              </Grid>
              <Grid item sm={7}>
                <Box mb={4}>
                  <TextValidatorComponent
                    label="Title"
                    validators={[VALIDATORS_MAP.required]}
                    onValidationChange={handleValidation}
                    value={settings.title ?? ''}
                    fieldName="title"
                    disableMinHeight
                    handleChange={handleChange}
                  />
                </Box>
                <Box mb={4}>
                  <TextValidatorComponent
                    label="Description"
                    validators={[VALIDATORS_MAP.required, VALIDATORS_MAP.maxStringLength(64)]}
                    onValidationChange={handleValidation}
                    value={settings.description ?? ''}
                    fieldName="description"
                    disableMinHeight
                    noWrap
                    handleChange={handleChange}
                  />
                  <Typography variant="caption" display="block">Recommended maximum characters amount is 64.</Typography>
                </Box>
                <Box mb={4}>
                  <TextValidatorComponent
                    label="Demo Sphere URL"
                    validators={[VALIDATORS_MAP.required, VALIDATORS_MAP.https('Sphere URL')]}
                    onValidationChange={handleValidation}
                    value={settings.demoSphereUrl ?? ''}
                    fieldName="demoSphereUrl"
                    disableMinHeight
                    handleChange={handleChange}
                  />
                </Box>
                <Box ml={-2.5} display="flex" alignItems="center">
                  <Switch
                    disabled={templatesList.length <= 0}
                    checked={settings.isDefault ?? false}
                    onChange={() => setToggleDialog(true)}
                  />
                  <Typography variant="caption">Set as default template</Typography>
                </Box>
              </Grid>
            </Grid>
          )}
        </CardComponent>
      </SettingsForm>
      <DefaultTemplateDialog
        templatesList={templatesList}
        templateSettings={settings}
        handleChange={handleChange}
        show={toggleDialog}
        setShow={val => setToggleDialog(val)}
      />
      <AlertDialog setShowError={setShowError} showError={showError} />
      {isPublished && (
        <Grid xs={12} sm={8} item>
          <ColoredBox variant="systemInfo">
            <ErrorOutline style={{ color: COLORS.systemInfo60, alignSelf: 'flex-start', marginTop: '8px', fontSize: '20px' }} />
            <Typography style={{ margin: '8px' }}>The Sphere needs to be Published in order to make it a template and access all settings.</Typography>
          </ColoredBox>
        </Grid>
      )}
    </>
  );
};

export default TemplateSettings;
